import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Seo } from "@/components/Seo";
import { Loader2, Mail } from "lucide-react";

export default function Unsubscribe() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [state, setState] = useState("loading"); // loading | ask | working | off | on | error

  useEffect(() => {
    if (!token) return;
    api
      .get("/email-status", { params: { token } })
      .then((r) => setState(r.data.alerts_off ? "off" : "ask"))
      .catch(() => setState("error"));
  }, [token]);

  const run = async (path, next) => {
    setState("working");
    try {
      await api.post(path, null, { params: { token } });
      setState(next);
    } catch {
      setState("error");
    }
  };

  const button =
    "inline-flex items-center justify-center gap-2 rounded-full bg-[#C89B3C] px-6 py-3 font-semibold text-black transition-transform hover:scale-105 disabled:opacity-60";

  return (
    <div className="mx-auto max-w-xl px-4 py-16 sm:px-6">
      <Seo title="Email alerts" noindex />
      <div className="rounded-2xl border border-[#C89B3C]/30 bg-[#161118]/80 p-8 text-center">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-[#C89B3C]/15 text-[#C89B3C]">
          <Mail className="h-6 w-6" />
        </div>

        {(!token || state === "error") && (
          <>
            <h1 className="mt-4 font-serif text-2xl font-semibold text-amber-50">This link did not work</h1>
            <p className="mt-3 text-amber-50/80">
              It may be out of date. Email <a className="text-[#C89B3C] hover:underline" href="mailto:hello@mytheatre.uk">hello@mytheatre.uk</a> and I will switch the emails off for you.
            </p>
          </>
        )}

        {token && state === "ask" && (
          <>
            <h1 className="mt-4 font-serif text-2xl font-semibold text-amber-50">Unsubscribe from alert emails?</h1>
            <p className="mt-3 text-amber-50/80">
              You will stop getting cast-change and booking emails. Your watchlist stays as it is.
            </p>
            <button type="button" className={`mt-6 ${button}`} onClick={() => run("/unsubscribe", "off")}>
              Unsubscribe
            </button>
          </>
        )}

        {token && (state === "working" || state === "loading") && (
          <div className="mt-6 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#C89B3C]" /></div>
        )}

        {token && state === "off" && (
          <>
            <h1 className="mt-4 font-serif text-2xl font-semibold text-amber-50">You are unsubscribed</h1>
            <p className="mt-3 text-amber-50/80">You will not get any more alert emails.</p>
            <button type="button" className="mt-5 text-sm text-[#C89B3C] hover:underline" onClick={() => run("/resubscribe", "on")}>
              Changed your mind? Turn emails back on
            </button>
          </>
        )}

        {token && state === "on" && (
          <>
            <h1 className="mt-4 font-serif text-2xl font-semibold text-amber-50">Emails are back on</h1>
            <p className="mt-3 text-amber-50/80">You will get alerts again for the shows and performers you follow.</p>
          </>
        )}

        <Link to="/" className="mt-6 inline-block text-sm text-amber-100/60 hover:text-amber-50">
          Back to My Theatre
        </Link>
      </div>
    </div>
  );
}